import { Button, Dialog, Field, Input, Label, ValidationMessage } from '@digdir/designsystemet-react';
import { useEffect, useRef, useState } from 'react';
import { postSubscriber } from '@/libs/data/classifications/subscriptionData';
import { localization } from '@/libs/language/src/localization';
import { clientLogger } from '@/libs/logger/client-logger';
import { SubscribeResult, Subscriber, SubscribeStatus, ValidationMessageColors } from '@/types/subscription';
import { messageByCode } from '@/utils/classifications/classificationHelpers';
import { validateEmailInput } from '@/utils/validators';
import styles from './subscribe.module.css';

const SubscribeDialog = ({ classificationId }: { classificationId: number | undefined }) => {
  const [inputValue, setInputValue] = useState('');
  const [subscriber, setSubscriber] = useState<Subscriber | null>(null);
  const [subscribeResult, setSubscribeResult] = useState<SubscribeResult | null>(null);
  const persistedResult = useRef<SubscribeResult | null>(null);

  const resetState = () => {
    setInputValue('');
    setSubscriber(null);
    setSubscribeResult(persistedResult.current);
  };

  useEffect(() => {
    if (subscriber) {
      postSubscriber(subscriber)
        .then((result) => {
          clientLogger.info('subscribeResult', result.message);
          persistedResult.current = result;
          setSubscribeResult(result);
        })
        .catch((error) => {
          clientLogger.error('Subscription failed', error);
        });
    }
  }, [subscriber]);

  const handleSubscription = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateEmailInput(inputValue)) {
      setSubscribeResult({
        code: SubscribeStatus.InvalidEmail,
        message: localization.classification.subscribeMessageInvalidEmail,
        dataColor: ValidationMessageColors.Warning,
      });
      return;
    }
    setSubscriber({ email: inputValue, classificationId });
  };

  return (
    <Dialog.TriggerContext>
      <Dialog.Trigger>{localization.classification.subscribe}</Dialog.Trigger>
      <Dialog onClose={resetState} closeButton={localization.close}>
        <form onSubmit={handleSubscription} noValidate>
          <Field className={styles.field}>
            <Label weight='semibold' htmlFor='subscription-email'>
              {localization.classification.subscribe}
            </Label>
            {persistedResult.current ? null : (
              <Field.Description>{localization.classification.subscribeInfo}</Field.Description>
            )}
            {persistedResult.current ? null : (
              <Input
                id='subscription-email'
                type='email'
                placeholder={localization.classification.emailPlaceholder}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setSubscribeResult(null);
                }}
              />
            )}
            {subscribeResult && (
              <ValidationMessage role='alert' data-color={subscribeResult.dataColor}>
                {messageByCode(subscribeResult.code)}
              </ValidationMessage>
            )}
          </Field>
          {persistedResult.current ? null : (
            <Button type='submit' id='subscribe-button'>
              {localization.classification.subscribeSubmit}
            </Button>
          )}
        </form>
      </Dialog>
    </Dialog.TriggerContext>
  );
};

export { SubscribeDialog };
